import { Lightbulb, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import {
  H2,
  P,
  Pull,
  Callout,
  ExampleTable,
  RolePair,
  RulesGrid,
  BigRule,
  Li,
} from "./_blocks";

export default function GstForGoldTraders() {
  return (
    <>
      <Pull>
        <strong>Quick answer:</strong> not all gold is treated the same for GST. Investment-grade
        bullion is exempt — no GST. Jewellery is standard 15% GST. The trap most gold traders
        fall into is treating both the same way.
      </Pull>

      <H2>The one rule that fixes 90% of the confusion</H2>
      <P>
        Gold buyers, pawnbrokers, jewellers, and bullion dealers all run into the same problem:
        their accountant gives them one GST answer, their supplier gives them another, and
        somewhere in the middle their margin disappears. The fix is small but precise:
      </P>

      <Callout tone="ink" icon={Lightbulb} kicker="The big idea">
        IRD treats <strong>fine gold</strong> (investment-grade bullion) as a financial-style
        asset — <strong>no GST</strong>. It treats <strong>jewellery and lower-purity gold</strong>{" "}
        as ordinary goods — <strong>15% GST applies</strong>. Two completely different worlds.
      </Callout>

      <H2>The two worlds, side by side</H2>
      <P>
        Most disputes between dealers and IRD come down to which bucket an item belongs in.
        Get this right at the moment you write the price tag, not after the audit:
      </P>

      <RolePair
        left={{
          accent: "green",
          kicker: "Fine gold (investment grade)",
          title: "NO GST charged",
          lines: [
            "Pure gold bars and bullion (typically 99.5%+).",
            "Investment-grade coins (recognised by IRD lists).",
            "Treated as exempt or zero-rated supply.",
            "Buyer pays the metal price — no 15% added.",
          ],
          verdict: "Buyer pays the spot price. Period.",
        }}
        right={{
          accent: "red",
          kicker: "Jewellery & low-purity gold",
          title: "15% GST applies",
          lines: [
            "Rings, chains, necklaces, scrap jewellery.",
            "9k, 14k, 18k items — anything below fine-metal purity.",
            "Treated as ordinary goods supply.",
            "Buyer pays the listed price + 15% GST on top.",
          ],
          verdict: "Standard NZ GST rules — 15% on top.",
        }}
      />

      <ExampleTable
        rows={[
          ["Bullion bar — listed price", "$3,000.00"],
          ["GST on bullion (exempt)", "$0.00"],
          ["Customer pays for bullion", "$3,000.00"],
        ]}
      />

      <ExampleTable
        rows={[
          ["18k ring — listed price", "$1,000.00"],
          ["GST on ring (15%)", "$150.00"],
          ["Customer pays for ring", "$1,150.00"],
        ]}
      />

      <H2>The second-hand gold trap</H2>
      <P>
        This is the one that hurts the most — and it catches almost every new gold buyer in
        New Zealand at least once.
      </P>
      <P>
        When you buy second-hand goods from a private seller (someone who isn't GST-registered),
        IRD usually lets you claim a <strong>notional GST input credit</strong> of 3/23 of the
        purchase price. So if you buy a TV for $2,300, you can claim $300 back in GST even though
        no GST was actually charged. Magic.
      </P>

      <Callout tone="warning" icon={AlertTriangle} kicker="The exclusion that catches gold buyers">
        <p className="m-0">
          <strong>Fine metals are explicitly excluded from the second-hand goods GST claim.</strong>{" "}
          You can claim it on jewellery (if it's not fine-metal pure), but you cannot claim it on
          investment-grade bullion. Many gold buyers claim 3/23 across the board and end up with
          a nasty surprise on their next IRD review.
        </p>
      </Callout>

      <div className="my-8 border border-slate-200 bg-slate-50">
        <table className="w-full text-sm md:text-base">
          <thead>
            <tr className="bg-[#0A192F] text-white">
              <th className="px-5 py-3 text-left font-heading font-bold">
                Buying second-hand from a private seller
              </th>
              <th className="px-5 py-3 text-right font-heading font-bold">Can you claim 3/23?</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["TV / tools / car / general goods", true],
              ["Jewellery (not fine-metal)", true],
              ["Pure gold bullion / investment coins", false],
            ].map(([label, ok], i) => (
              <tr key={label} className={i % 2 ? "bg-white" : "bg-slate-50"}>
                <td className="px-5 py-3 border-b border-slate-200">{label}</td>
                <td className="px-5 py-3 border-b border-slate-200 text-right">
                  {ok ? (
                    <span className="inline-flex items-center gap-2 text-emerald-600 font-heading font-bold uppercase text-xs tracking-[0.12em]">
                      <CheckCircle2 className="w-4 h-4" /> Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-red-600 font-heading font-bold uppercase text-xs tracking-[0.12em]">
                      <XCircle className="w-4 h-4" /> No
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>The three situations every gold trader faces</H2>

      <Callout tone="success" icon={CheckCircle2} kicker="Situation A · Buying jewellery">
        <p className="m-0 mb-2">
          You buy a chain from the public for <strong>$1,000</strong>. You can claim 3/23 = $130.43
          input GST. You polish it up and sell it for <strong>$1,500 + GST = $1,725</strong>.
        </p>
        <p className="m-0 text-sm text-slate-600">
          Margin: $500 + GST. GST flows through the books cleanly. This is how it's <em>supposed</em>{" "}
          to work.
        </p>
      </Callout>

      <Callout tone="gold" icon={Lightbulb} kicker="Situation B · Buying bullion">
        <p className="m-0 mb-2">
          You buy a 1oz gold bar at <strong>$3,000</strong>. No GST claimed (it's exempt). You sell
          for <strong>$3,200</strong>. No GST charged.
        </p>
        <p className="m-0 text-sm text-slate-600">
          Margin: $200. GST never enters the picture. Your books should record it as an{" "}
          <strong>exempt supply</strong>, not a zero-rated normal sale.
        </p>
      </Callout>

      <Callout tone="warning" icon={AlertTriangle} kicker="Situation C · The mixed-up dealer">
        <p className="m-0 mb-2">
          You assume <em>"all gold is GST-free"</em> (or <em>"all gold has 15% GST"</em>) and apply
          one rule to everything. Either you over-claim input GST on bullion you can't claim, or
          you forget to charge GST on jewellery you should have.
        </p>
        <p className="m-0 text-sm text-slate-600">
          IRD's audit team will spot this within minutes when they pull your purchase records
          against your sales. The penalty isn't just the unpaid GST — it's the use-of-money
          interest stacked on top.
        </p>
      </Callout>

      <H2>The pricing rule (write this on the wall)</H2>
      <P>
        Pricing in this niche isn't about the metal — it's about the <em>category</em>. Get the
        category right and the pricing maths takes care of itself.
      </P>

      <BigRule
        green={{ label: "Pure / investment gold", value: "NO GST" }}
        red={{ label: "Jewellery & lower-purity gold", value: "15% GST applies" }}
      />

      <H2>The 3 rules to never lose money on a gold transaction</H2>
      <RulesGrid
        rules={[
          {
            n: "01",
            title: "Classify before you price",
            body: "Decide the category before you write the tag. Fine metal vs jewellery is the only question that matters for GST. Doing it after the sale means rebuilding the invoice.",
          },
          {
            n: "02",
            title: "Tag exempt sales as exempt",
            body: "When you sell bullion, your books need to mark it exempt — not zero-rated, not GST-inclusive. Wrong tag = wrong GST101 line, and IRD notices.",
          },
          {
            n: "03",
            title: "Never claim 3/23 on bullion",
            body: "If the second-hand item is fine-metal gold, the 3/23 input credit doesn't apply. Build a small purchase checklist that asks 'is it fine metal?' before the journal posts.",
          },
        ]}
      />

      <H2>Why this matters for your margin</H2>
      <P>
        In gold trading, GST mistakes don't show up in your tax return — they show up in your{" "}
        <strong>margin</strong>. Three patterns we see most often:
      </P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="bad">
          Claiming 3/23 on bullion purchases. Margin <em>looks</em> bigger until IRD claws it back
          a year later, often with interest.
        </Li>
        <Li tone="bad">
          Forgetting to charge 15% GST on broken / scrap jewellery refined into resale items —
          that's a sale of jewellery, not bullion, even if the gold inside it is high purity.
        </Li>
        <Li tone="bad">
          Treating dealer-to-dealer bullion swaps as ordinary sales when they should be exempt.
          Inflates your turnover and your apparent GST liability.
        </Li>
      </ul>

      <Callout tone="ink" icon={Lightbulb} kicker="The accountant's one-liner">
        <p className="m-0">
          "GST mistakes in gold trading don't happen in accounting — they happen at the moment
          you put the price tag on. Get the classification right and the books look after
          themselves."
        </p>
      </Callout>

      <H2>What this looks like for you</H2>
      <P>
        We work with several NZ gold buyers and pawn-style dealers on monthly bookkeeping. The
        repeatable wins:
      </P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="good">
          Set up your Xero with{" "}
          <strong>two distinct sales tax codes</strong> — one for exempt fine-metal sales, one
          for standard-rated jewellery. Reconciling becomes a 5-minute job, not a nightmare.
        </Li>
        <Li tone="good">
          Build a one-question check at the buying counter:{" "}
          <em>"Is this fine-metal gold?"</em> — then the till receipt classifies it for you.
        </Li>
        <Li tone="good">
          Email you a <strong>monthly GST position</strong> with exempt vs taxable supplies on
          separate lines, so the GST101 return is a copy-paste — not a project.
        </Li>
      </ul>
    </>
  );
}
